import type { OnDestroy } from '@angular/core';
import { Injectable } from '@angular/core';
import { initialize, type LDClient } from 'launchdarkly-js-client-sdk';
import type { Observable } from 'rxjs';
import { BehaviorSubject, fromEventPattern, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { DefaultFlagSet } from './constants/default-flag-set.constant';
import type { Flag } from './constants/flag.enum';
import type { FeatureFlagUser } from './interfaces/feature-flag-user.interface';
import type { FlagSet } from './interfaces/flag-set.interface';
import type { FlagChangeSet } from './types/flag-change-set.type';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService implements OnDestroy {
  /**
   * Subject for destruction of the service.
   */
  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  /**
   * Mapping of flag keys to their behavior subjects.
   */
  private readonly changeMap: Map<Flag, BehaviorSubject<FlagSet[Flag]>> = new Map();

  /**
   * The flag set.
   */
  public readonly flags$: BehaviorSubject<FlagSet> = new BehaviorSubject(DefaultFlagSet);

  /**
   * An observable tracking whether the LaunchDarkly client is ready.
   */
  public readonly ldReady$: Observable<boolean>;

  /**
   * An observable to LaunchDarkly client flag set changes listening to the 'change' event.
   */
  public readonly ldChanges$: Observable<FlagChangeSet>;

  /**
   * The configuration for LaunchDarkly.
   */
  private readonly config = environment.launchDarkly;

  /**
   * Load state.
   */
  private readonly flagsServiceLoaded: BehaviorSubject<boolean> = new BehaviorSubject(false);

  /**
   * Emits once when real flag values have been pulled from the LD network.
   * Distinct from flagsServiceLoaded/ready, which fires immediately when bootstrap is set.
   *
   * Edge case: if all flag values returned by LD match the bootstrap defaults, LD will not
   * fire a 'change' event, and this subject will never emit — causing getFlagByType() to hang
   * indefinitely. If that becomes a problem, a timeout/fallback should be added here.
   */
  private readonly ldNetworkReady$: ReplaySubject<true> = new ReplaySubject(1);


  /**
   * The LaunchDarkly client.
   */
  public readonly client: LDClient;

  /**
   * Constructor.
   */
  constructor() {
    // set the flags from the default flags in case launch darkly doesn't have the flags defined in the defaults
    this.flags = DefaultFlagSet;
    const options = {
      ...this.config.options,
      bootstrap: DefaultFlagSet, // set the default flags as initial values
      sendLDHeaders: false, // avoid preflight requests for performance
      streaming: true, // stream by default
      sendEventsOnlyForVariation: true, // avoid sending events for allFlags
    };

    // Initialize LaunchDarkly with an anonymous userless context.
    // The SDK will generate and persist a device-specific key automatically for anonymous contexts.
    this.client = initialize(
      this.config.clientSideId,
      { kind: 'user', anonymous: true, key: '__anonymous' },
      options,
    );

    // listen for readiness
    this.ldReady$ = fromEventPattern(
      (handler) => {
        this.client.on('ready', handler);
      },
      (handler) => {
        this.client.off('ready', handler);
      },
    );

    // listen for all changes
    this.ldChanges$ = fromEventPattern(
      (handler) => {
        this.client.on('change', handler);
      },
      (handler) => {
        this.client.off('change', handler);
      },
    );

    this.listen();
  }

  /**
   * Subscribe to the events.
   */
  private listen(): void {
    this.ldReady$
      .pipe(
        takeUntil(this.destroyed$),
        tap(() => {
          // this.flags = this.client.allFlags() as FlagSet;
          // instead of getting flags from allFlags(), which evaluates all flags, retrieve them from the known flags.

          // map the current values in the changed flags to a flag set
          const flags: any = {};

          // break down the keys of the default flag set
          const keys = Object.keys(this.flags) as Flag[];

          keys.forEach((key) => {
            flags[key] = this.client.variation(key, this.flags[key]);
          });

          // set
          this.flags = flags as Partial<FlagSet>;
          this.flagsServiceLoaded.next(true);
        }),
      )
      .subscribe();

    this.ldChanges$
      .pipe(
        takeUntil(this.destroyed$),
        tap((changedFlags) => {
          // map the current values in the changed flags to a flag set
          const flags: any = {};

          // break down the keys
          const keys = Object.keys(changedFlags) as Flag[];

          // loop through each and overwrite
          keys.forEach((key) => {
            flags[key] = changedFlags[key].current;
          });

          this.flags = flags as Partial<FlagSet>;

          // Real LD values have arrived from the network — signal that flags are ready for use.
          this.ldNetworkReady$.next(true);
        }),
      )
      .subscribe();
  }

  /**
   * Set flags in a patching method.
   */
  public set flags(newFlags: Partial<FlagSet>) {
    // get the current flags
    const flags: any = this.flags$.value;

    // break down the keys
    const keys = Object.keys(newFlags) as Flag[];

    // loop through each and overwrite
    keys.forEach((key) => {
      // set the value
      const value = (newFlags as any)[key];

      // set the key with value (cast as non partial to avoid type errors)
      flags[key] = value;

      // set the observable
      if (this.changeMap.has(key)) {
        const subject = this.changeMap.get(key) as BehaviorSubject<typeof value>;

        if (subject !== undefined) {
          subject.next(value);
        }
      } else {
        const subject = new BehaviorSubject<typeof value>(value);

        this.changeMap.set(key, subject);
      }
    });

    this.flags$.next(flags);
  }

  /**
   * Get flags.
   */
  public get flags() {
    return this.flags$.value;
  }

  /**
   * Observe specific events for a specific flag.
   *
   * @param key The flag key.
   * @returns An observable for a behavior subject.
   */
  public observe<T extends Flag>(key: T): Observable<FlagSet[T]> {
    const subject = this.changeMap.get(key) as BehaviorSubject<FlagSet[T]>;
    if (subject !== undefined) {
      // only observe changes to the subject to avoid thrashing
      return subject.asObservable().pipe(distinctUntilChanged());
    }

    throw new Error('flag key and subject not defined');
  }

  /**
   * Get observable if the flag service has finished loaded.
   *
   * @returns An observable specifying whether the flags service has loaded.
   */
  public getFlagServiceReady(): Observable<boolean> {
    return this.flagsServiceLoaded.asObservable();
  }

  /**
   * Identifies the user to LaunchDarkly after login.
   * This method should be called after successful user authentication to switch from anonymous to identified user.
   *
   * @param user - The user information for LaunchDarkly identification.
   */
  public async login(user: FeatureFlagUser): Promise<void> {
    try {
      // Wait for the client to be ready
      await this.client.waitForInitialization(5);

      // Create the LaunchDarkly user object
      const ldUser: FeatureFlagUser = {
        ...user,
        anonymous: false,
      };
      // Identify the user to LaunchDarkly (switches from anonymous to identified user)
      const flags = await this.client.identify(ldUser);
      // Update the flags with user-specific values
      this.flags = flags as FlagSet;

      // Mark the service as loaded with user-specific flags
      this.flagsServiceLoaded.next(true);
      this.ldNetworkReady$.next(true);
    } catch (error) {
      this.flags = DefaultFlagSet;
      console.error('Error identifying user to LaunchDarkly:', error);
    }
  }

  /**
   * Closes the LaunchDarkly connection.
   */
  public async ngOnDestroy(): Promise<void> {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    await this.client.close();
  }

  public getFlagByType<T = boolean>(flagType: Flag): Observable<T> {
    return this.ldNetworkReady$.pipe(
      take(1),
      switchMap(() => this.observe<typeof flagType>(flagType).pipe(take(1)) as Observable<T>),
    );
  }
}
